import {
  createRow,
  getMemoOptions,
  memo,
  Row,
  RowModel,
  Table,
} from '@tanstack/react-table';

export function getCustomCoreRowModel(): (
  table: Table<any>
) => () => RowModel<any> {
  return (table) =>
    memo(
      () => [
        table.options.data,
        table.options.getLock,
        table.options.entityType,
        table.options.prepareRowDisplayModifiers,
      ],
      (
        data,
        getLock,
        entityType,
        prepareRowDisplayModifiers
      ): {
        rows: Row<any>[];
        flatRows: Row<any>[];
        rowsById: Record<string, Row<any>>;
      } => {
        const rowModel: RowModel<any> = {
          rows: [],
          flatRows: [],
          rowsById: {},
        };

        const accessRows = (
          originalRows: any[],
          depth = 0,
          parentRow?: Row<any>
        ): Row<any>[] => {
          const rows = [] as Row<any>[];

          for (let i = 0; i < originalRows.length; i++) {
            let original = originalRows[i]!;

            if (getLock && original?.id) {
              const lock = getLock(original.id, entityType);
              if (lock && lock.lockedBy) {
                original = { ...original, objectLock: lock.lockedBy.id };
              }
            }

            // Make the row
            const row = createRow(
              table,
              table._getRowId(original, i, parentRow),
              original,
              i,
              depth,
              undefined,
              parentRow?.id
            );

            row.meta = {};

            if (prepareRowDisplayModifiers && row.original) {
              row.displayModifiers = prepareRowDisplayModifiers(row.original);
            }

            // Keep track of every row in a flat array
            rowModel.flatRows.push(row);
            // Also keep track of every row by its ID
            rowModel.rowsById[row.id] = row;
            // Push table row into parent
            rows.push(row);

            // Get the original subrows
            if (table.options.getSubRows) {
              row.originalSubRows = table.options.getSubRows(original, i);

              // Then recursively access them
              if (row.originalSubRows?.length) {
                row.subRows = accessRows(row.originalSubRows, depth + 1, row);
              }
            }
          }

          return rows;
        };

        rowModel.rows = accessRows(data);

        return rowModel;
      },
      getMemoOptions(table.options, 'debugTable', 'getRowModel', () =>
        table._autoResetPageIndex()
      )
    );
}
